import { useShow } from '@refinedev/core';
import { IUserWithProfile } from '../../interfaces/user';
import { List, ShowButton, useTable } from '@refinedev/antd';
import { Card, Col, Form, Layout, Row, Space, Switch, Table, Tabs, Typography } from 'antd';
import { IChild } from '../../interfaces/child';
import {
  AimOutlined,
  GoogleOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ImpersonateButton } from './impersonate-button';
import { SkipEmailVerifyButton } from './skip-email-verify-button';
import { fetcher } from '../../utils/fetcher';
import { useState } from 'react';

export const UserShow = () => {
  const [toggleSittingLoading, setToggleSittingLoading] = useState(false);
  const { queryResult } = useShow<IUserWithProfile>({
    meta: {
      fields: [
        'id',
        'email',
        'firebaseId',
        'useSitting',
        'createdAt',
        'updatedAt',
        {
          profile: [
            'name',
            'kana',
            'tel',
            'gender',
            'postalCode',
            'prefecture',
            'city',
            'address',
            'building',
          ],
        },
      ],
    },
  });
  const { data, isLoading } = queryResult;
  const user = data?.data;

  const { tableProps: childTableProps } = useTable<IChild>({
    resource: 'children',
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    permanentFilter: [{ field: 'userId', operator: 'eq', value: user?.id }],
    syncWithLocation: false,
    meta: {
      fields: ['id', 'name', 'gender', 'birthday', 'createdAt'],
    },
  });

  return (
    <Row gutter={[16, 16]} style={{}}>
      <Col xl={8} lg={24} xs={24}>
        <Card dir="column" loading={isLoading}>
          <Space direction="vertical">
            <Typography.Text>
              <UserOutlined /> <Typography.Text code>{user?.id}</Typography.Text>
            </Typography.Text>
            <Typography.Text disabled={!user?.email}>
              <MailOutlined /> <Typography.Text code>{user?.email}</Typography.Text>
            </Typography.Text>
            <Typography.Text disabled={!user?.profile}>
              <UserOutlined /> {user?.profile?.name || '未設定'}({user?.profile?.kana || '未設定'})
            </Typography.Text>
            <Typography.Text disabled={!user?.profile?.tel}>
              <PhoneOutlined /> {user?.profile?.tel || '未設定'}
            </Typography.Text>
            <Typography.Text disabled={!user?.profile?.postalCode}>
              〒 {user?.profile?.postalCode || '未設定'}
            </Typography.Text>
            <Typography.Text disabled={!user?.profile?.prefecture}>
              <AimOutlined />{' '}
              {`${user?.profile?.prefecture || '未設定'} ${user?.profile?.city || ''} ${
                user?.profile?.address || ''
              } ${user?.profile?.building || ''}`}
            </Typography.Text>
            <Typography.Text>
              <GoogleOutlined /> <Typography.Text code>{user?.firebaseId}</Typography.Text>
            </Typography.Text>
            <Space style={{ marginTop: 20 }}>
              <ImpersonateButton userId={user?.id} />
              <SkipEmailVerifyButton userId={user?.id} />
              <Space style={{ cursor: 'pointer' }}>
                <label htmlFor="useSitting" style={{ cursor: 'pointer' }}>
                  <Typography.Text>シッター利用</Typography.Text>
                </label>
                <Switch
                  id="useSitting"
                  checked={user?.useSitting}
                  loading={toggleSittingLoading}
                  onChange={async () => {
                    setToggleSittingLoading(true);
                    await fetcher(`/admin/users/${user?.id}/toggle_use_sitting`, {
                      method: 'PUT',
                    });
                    queryResult.refetch();
                    setToggleSittingLoading(false);
                  }}
                />
              </Space>
            </Space>
          </Space>
        </Card>
      </Col>

      <Col xl={16} lg={24} xs={24}>
        <List
          title="子供"
          headerProps={{
            extra: <></>,
          }}
        >
          <Table
            {...childTableProps}
            rowKey="id"
            pagination={{
              ...childTableProps.pagination,
              showSizeChanger: true,
              showTotal: (total) => `全${total}件`,
            }}
          >
            <Table.Column dataIndex="id" title="ID" />
            <Table.Column dataIndex="name" title="Name" />
            <Table.Column dataIndex="gender" title="Gender" />
            <Table.Column dataIndex="birthday" title="Birthday" />
            <Table.Column dataIndex="createdAt" title="CreatedAt" />
            <Table.Column<IChild>
              dataIndex="actions"
              render={(_, record) => (
                <ShowButton hideText size="small" resource="children" recordItemId={record.id} />
              )}
            />
          </Table>
        </List>
      </Col>
    </Row>
  );
};
